import { ReactNode } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "wouter";
import Navbar from "./Navbar";
import Footer from "./Footer";
import { SITE_ORIGIN } from "@/lib/siteData";

const LEGACY_ORIGIN = "https://locranking-7q2qajau.manus.space";
const DEFAULT_OG_IMAGE = `${SITE_ORIGIN}/og-default.jpg`;

interface HreflangEntry {
  hreflang: string;
  href: string;
}

interface LayoutProps {
  children: ReactNode;
  title?: string;
  description?: string;
  canonical?: string;
  ogImage?: string;
  schema?: object | object[];
  /** hreflang alternate links for multilingual pages */
  hreflangLinks?: HreflangEntry[];
}

function normalizeUrl(url: string): string {
  return url
    .replace(LEGACY_ORIGIN, SITE_ORIGIN)
    .replace("https://www.localrankingcoach.com", SITE_ORIGIN);
}

export default function Layout({
  children,
  title,
  description,
  canonical,
  ogImage,
  schema,
  hreflangLinks,
}: LayoutProps) {
  const [location] = useLocation();
  const pathname = location.split("?")[0] || "/";
  const canonicalUrl = normalizeUrl(canonical ?? `${SITE_ORIGIN}${pathname}`);
  const imageUrl = ogImage ? normalizeUrl(ogImage) : DEFAULT_OG_IMAGE;

  const schemas = schema ? (Array.isArray(schema) ? schema : [schema]) : [];

  return (
    <div className="min-h-screen flex flex-col">
      <Helmet>
        {title && <title>{title}</title>}
        {description && <meta name="description" content={description} />}
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Local Ranking Coach" />
        {title && <meta property="og:title" content={title} />}
        {description && <meta property="og:description" content={description} />}
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:locale" content="en_US" />
        <meta property="og:image" content={imageUrl} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@LocalRankCoach" />
        {title && <meta name="twitter:title" content={title} />}
        {description && <meta name="twitter:description" content={description} />}
        <meta name="twitter:image" content={imageUrl} />
        {hreflangLinks?.map(({ hreflang, href }) => (
          <link key={hreflang} rel="alternate" hrefLang={hreflang} href={normalizeUrl(href)} />
        ))}
      </Helmet>

      {/* JSON-LD Schema Markup */}
      {schemas.map((s, i) => (
        <script
          key={i}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(s) }}
        />
      ))}
      <Navbar />
      <main id="main-content" className="flex-1" tabIndex={-1}>
        {children}
      </main>
      <Footer />
    </div>
  );
}
